import {
  Component,
  ContentChild,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output, SimpleChanges,
  TemplateRef, ViewChild
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {ButtonModule} from "../../../modules/core/component/button/button.module";
import {KeepHtmlPipeModule} from "../../../modules/core/pipe/keep-html.pipe";
import {LoadingModule} from "../loading/loading.module";
import {MatFormFieldModule} from "@angular/material/form-field";
import {MatIconModule} from "@angular/material/icon";
import {MatOptionModule} from "@angular/material/core";
import {MatSelect, MatSelectModule} from "@angular/material/select";
import {NgxMatSelectSearchModule} from "ngx-mat-select-search";
import {ContentDirective} from "../../../shared/directives/content.directive";
import {ReactiveFormsModule, UntypedFormControl, UntypedFormGroup} from "@angular/forms";
import {BehaviorSubject, ReplaySubject, Subject, Subscription} from "rxjs";
import {
  AbstractSelectComponent,
  NewItemModalOptions
} from "../../../modules/core/component/select/abstract-select.component";
import {FormUtils} from "../../../shared/utils/form.utils";
import {SelectElementV2} from "../select/selectElementV2";
import {takeUntil} from "rxjs/operators";
import {MaterialModule} from "../../../material.module";

@Component({
  selector: 'app-select-filter-v2',
  standalone: true,
  imports: [CommonModule, ButtonModule, KeepHtmlPipeModule, LoadingModule, MatFormFieldModule, MatIconModule, MatOptionModule, MatSelectModule, NgxMatSelectSearchModule, ReactiveFormsModule, MaterialModule],
  templateUrl: './select-filter-v2.component.html',
  styles: [
  ]
})
export class SelectFilterV2Component<E> extends AbstractSelectComponent<number | string> implements OnInit, OnDestroy, OnChanges {

  @ContentChild(ContentDirective, {static: true, read: TemplateRef}) template: TemplateRef<any>;

  @Input() disabled = false;
  @Input() loading = false;
  @Input() parentFormGroup: UntypedFormGroup;
  @Input() noEntriesFoundLabel: string;
  @Input() placeHolderLabel: string;
  @Input() parentFormControlName: string;
  @Input() elements: SelectElementV2<E>[] = [];
  @Input() filterElementProcessor: (criteria: string, element: SelectElementV2<E>) => boolean;
  @Input() creationEnable = false;
  @Input() deletable = false;
  @Input() noValueLabel: string;
  @Input() newItemModalOptions: NewItemModalOptions<any, any, any>;
  @Input() createButtonLabel: string;
  @Input() errorsMessages: { [key: string]: string };

  @Output() focussedEvent: EventEmitter<void> = new EventEmitter<void>();
  @Output() _onSingleSelectionChange = new EventEmitter<SelectElementV2<E>>();

  @ViewChild('matSelect') matSelect: MatSelect;

  filteredElements: SelectElementV2<E>[] = [];
  selectedElement: SelectElementV2<E>;
  eraseSelectionEnabled = false;
  /** control for filter */
  filterCtrl: UntypedFormControl = new UntypedFormControl();
  subscription: Subscription;
  private destroy$ = new Subject<void>();

  constructor() {
    super();
  }


  ngOnInit() {
    this.filterCtrl.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          this.filter();
        });
    this.elementControl.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          this.setSelected();
        });

    this.eraseSelectionEnabled = this.deletable && !FormUtils.isRequiredField(this.elementControl);
  }

  setSelected() {
    this.selectedElement = undefined;
    if (this.elementControl) {
      const elements = this.elements.filter(item => item.value === this.elementControl.value);
      if (elements && elements.length === 1) {
        this.selectedElement = elements[0];
      }
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['parentFormGroup'] || changes['parentFormControlName']) {
      this.subscription?.unsubscribe();
      if (this.parentFormControlName && this.parentFormGroup) {
        this.subscription = this.parentFormGroup.controls[this.parentFormControlName].valueChanges.subscribe(value => {
          // console.logByType'maxime value changed');
          this.setSelected();
          this.onSelectionChange(value);
        });
      }
    }
    if (changes['elements']) {
      this.setSelected();
      this.filter();
    }
  }

  private filter() {
    if (!this.elements) {
      this.filteredElements = [];
      return;
    }
    const criteria = this.filterCtrl.value;
    if (!criteria) {
      this.filteredElements = this.elements;
      return;
    }
    this.filteredElements = this.elements.filter(value => this.filterElements(criteria, value));
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get elementControl() {
    return this.parentFormGroup?.get(this.parentFormControlName);
  }

  onSelectionChange(value: any) {
    if (value) {
      this._onSingleSelectionChange.next(this.elements.find(item => item.value === value));
    } else {
      this._onSingleSelectionChange.next(null);
    }
  }

  filterElements = (criteria: string, element: SelectElementV2<E>) => {
    if (this.filterElementProcessor) {
      return this.filterElementProcessor(criteria, element);
    } else {
      return element.label.replace(/<[^>]*>/g, '').toUpperCase().includes(criteria.toUpperCase());
    }
  }

  eraseSelection() {
    this.filterCtrl.setValue(null);
    this.elementControl.setValue(null);
    this._onSingleSelectionChange.emit(null);
  }

  addNewItem(value: any) {
    this.elementControl.setValue(value);
  }

  closeSelect() {
    this.matSelect.close();
  }

  getSelectInputValue(): string {
    return this.filterCtrl.value;
  }

  errorsKey(): string[] {
    const errors = FormUtils.collectErrors(this.elementControl);
    if (errors) {
      return Object.keys(errors);
    }
  }

  extractErrorMessage(errorKey: string) {
    if (this.errorsMessages && this.errorsMessages[errorKey]) {
      return this.errorsMessages[errorKey];
    }
    if (errorKey === 'required') {
      return 'La valeur est obligatoire';
    }
    return 'La valeur n\'est pas correcte';
  }

  focussed() {
    this.focussedEvent.emit();
  }
}
